import React, {FC, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import CancelSharpIcon from '@mui/icons-material/CancelSharp'
import FileCopySharpIcon from '@mui/icons-material/FileCopySharp'
import FolderSharpIcon from '@mui/icons-material/FolderSharp'
import InfoSharpIcon from '@mui/icons-material/InfoSharp'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import {Box, Divider, Grid, Tooltip} from '@mui/material'
import {Theme} from '@mui/material/styles'
import {PGButton, PGTypography} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import {useResponsive} from '@progyny/hooks/useResponsive'
import {FileMax} from '@progyny/pages/Budget/Reimbursement/Documents'

export interface FilePreviewProps {
  file: File
  docType: string
  description?: string
  removeFile: React.Dispatch<File>
  hideFileSize?: boolean
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    background: theme.palette.common.white,
  },
  validItem: {
    color: theme.palette.text.primary,
  },
  fileIcon: {
    color: theme.palette.primary.main,
  },
  validIcon: {
    color: '#B6BAC8',
  },
  errorRow: {
    marginTop: theme.spacing(-3),
  },
  fileName: {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  bytes: {
    fontSize: theme.typography.caption.fontSize,

    color: theme.palette.text.muted,
  },
  removeIcon: {
    color: theme.palette.secondary.main,
    [theme.breakpoints.up('md')]: {
      color: theme.palette.secondary.main,
      '&:hover': {
        color: theme.palette.secondary.light,
      },
    },
  },
  removeIconContainer: {
    button: {
      borderRadius: 3,
      cursor: 'pointer',
    },
  },
  description: {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  filesizeError: {
    color: theme.palette.error.main,
  },
}))

export const FilePreview: FC<FilePreviewProps> = (props) => {
  const {file, docType, description, removeFile} = props
  const {classes, cx} = useStyles()
  const {t} = useTranslation()
  const isDownSM = useResponsive('down', 'sm')
  const removeFileName = t('file_preview_remove_file', {fileName: file.name})

  const isFileOverMax = useMemo<boolean>(() => file.size > FileMax, [file.size])

  const navigateToPreview = (file: File) => {
    const fileURL = URL.createObjectURL(file)
    window.open(fileURL, '_blank')
  }

  return (
    <>
      <Grid
        container
        spacing={3}
        mb={2}
        pb={2}
        alignItems={'center'}
        className={classes.root}
      >
        {!isDownSM && (
          <Grid item xs={4}>
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'flex-start'}
              alignItems={'center'}
              className={isFileOverMax ? classes.filesizeError : undefined}
            >
              <Box mr={1} mt={0.5}>
                <FolderSharpIcon
                  className={
                    isFileOverMax ? classes.filesizeError : classes.validIcon
                  }
                />
              </Box>
              <Box>
                <PGTypography variant={'caption'}>{t(docType)}</PGTypography>
              </Box>
            </Box>
          </Grid>
        )}
        <Grid item xs={4}>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'flex-start'}
            alignItems={'center'}
            sx={{cursor: 'pointer', minWidth: 0}}
            onClick={() => navigateToPreview(file)}
          >
            <Box mr={1} mt={0.5} sx={{flexShrink: 0}}>
              {isFileOverMax ? (
                <CancelSharpIcon className={classes.filesizeError} />
              ) : (
                <FileCopySharpIcon className={classes.fileIcon} />
              )}
            </Box>
            <Box sx={{minWidth: 0, flex: 1}}>
              {isFileOverMax ? (
                <PGTypography
                  variant={'caption'}
                  className={cx(classes.fileName, classes.filesizeError)}
                >
                  {t('file_preview_this_file_exceeds_20mb_size')}
                </PGTypography>
              ) : (
                <Tooltip title={file.name}>
                  <Box sx={{overflow: 'hidden', minWidth: 0}}>
                    <PGTypography
                      variant={'caption'}
                      className={classes.fileName}
                      data-testid={'file-name'}
                    >
                      {file.name}
                    </PGTypography>
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={4} sm={4}>
          {description && (
            <Box
              display={'flex'}
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'flex-start'}
              sx={{minWidth: 0}}
            >
              <Box
                display={'flex'}
                flexDirection={'row'}
                alignItems={'center'}
                mr={1}
                sx={{flexShrink: 0}}
              >
                <InfoSharpIcon className={classes.validIcon} />
              </Box>
              <Tooltip title={description}>
                <Box sx={{overflow: 'hidden', minWidth: 0}}>
                  <PGTypography
                    variant={'caption'}
                    className={classes.description}
                  >
                    {description}
                  </PGTypography>
                </Box>
              </Tooltip>
            </Box>
          )}
        </Grid>
      </Grid>
      <Grid item xs={4} sm={2} className={classes.removeIconContainer}>
        <Box>
          <Tooltip title={removeFileName}>
            <span>
              <PGButton
                variant={'outlined'}
                onClick={() => removeFile(file)}
                startIcon={<RemoveCircleOutlineIcon />}
                label={t('common_remove')}
                aria-label={removeFileName}
              />
            </span>
          </Tooltip>
        </Box>
      </Grid>
      {!props?.hideFileSize && <Divider sx={{my: 3}} />}
    </>
  )
}
